import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Instagram, Linkedin, ChevronDown } from 'lucide-react';

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [isLight, setIsLight] = useState(true);
  const [filmsDropdownOpen, setFilmsDropdownOpen] = useState(false);
  const [photoDropdownOpen, setPhotoDropdownOpen] = useState(false);
  const location = useLocation();

  // Check if we're on a project page (dark bg) or photography page (cream bg)
  const isFilmProjectPage = location.pathname.includes('/project/');
  const isPhotographyPage = location.pathname.includes('/photography/');

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
      
      // Only do section-based color detection on homepage
      if (location.pathname === '/') {
        const scrollPosition = window.scrollY + 60;
        const contactSection = document.getElementById('contact');
        
        const contactTop = contactSection ? contactSection.offsetTop : Infinity;
        const contactBottom = contactSection ? contactSection.offsetTop + contactSection.offsetHeight : Infinity;
        
        if (scrollPosition >= contactTop && scrollPosition < contactBottom) {
          setIsLight(false);
        } else {
          setIsLight(true);
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [location.pathname]);

  // On film project pages (dark bg), use light (cream) text
  // On photography pages (cream bg), use dark (green) text
  useEffect(() => {
    if (isFilmProjectPage) {
      setIsLight(true);
    } else if (isPhotographyPage) {
      setIsLight(false);
    }
  }, [isFilmProjectPage, isPhotographyPage]);

  const scrollToSection = (sectionId) => {
    if (location.pathname !== '/') {
      // Navigate to home with hash - instant jump, no scroll
      window.location.href = `/#${sectionId}`;
    } else {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'instant' });
      }
    }
  };

  const textColor = isLight ? 'text-cream' : 'text-dark';
  const dropdownBg = isLight ? 'bg-dark/95' : 'bg-cream/95';
  const dropdownText = isLight ? 'text-cream' : 'text-dark';

  return (
    <header
      data-testid="main-header"
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        scrolled ? 'py-3' : 'py-4'
      }`}
    >
      <div className="container mx-auto px-6 md:px-12 max-w-[1600px]">
        <div className="flex items-center justify-between">
          <nav data-testid="left-nav" className="flex items-center gap-8">
            {/* Films with Dropdown */}
            <div 
              className="relative"
              onMouseEnter={() => setFilmsDropdownOpen(true)}
              onMouseLeave={() => setFilmsDropdownOpen(false)}
            >
              <button
                onClick={() => scrollToSection('projects')}
                data-testid="films-link"
                className={`nav-link text-sm uppercase tracking-wider ${textColor} hover:opacity-70 transition-all duration-300 flex items-center gap-1`}
              >
                Films
                <ChevronDown size={14} className={`transition-transform duration-200 ${filmsDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {/* Dropdown Menu */}
              <div 
                className={`absolute top-full left-0 mt-2 py-2 min-w-[180px] rounded-lg shadow-lg transition-all duration-200 ${dropdownBg} ${
                  filmsDropdownOpen ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-2'
                }`}
              >
                <Link
                  to="/project/dress-codes"
                  className={`block px-4 py-2 text-sm ${dropdownText} hover:bg-white/10 transition-colors`}
                >
                  Dress Codes
                </Link>
                <Link
                  to="/project/future-observatory"
                  className={`block px-4 py-2 text-sm ${dropdownText} hover:bg-white/10 transition-colors`}
                >
                  Future Observatory
                </Link>
                <Link
                  to="/project/princes-in-tower"
                  className={`block px-4 py-2 text-sm ${dropdownText} hover:bg-white/10 transition-colors`}
                >
                  Princes in the Tower
                </Link>
              </div>
            </div>

            {/* Photography with Dropdown */}
            <div 
              className="relative"
              onMouseEnter={() => setPhotoDropdownOpen(true)}
              onMouseLeave={() => setPhotoDropdownOpen(false)}
            >
              <button
                onClick={() => scrollToSection('photography')}
                data-testid="photos-link"
                className={`nav-link text-sm uppercase tracking-wider ${textColor} hover:opacity-70 transition-all duration-300 flex items-center gap-1`}
              >
                Photography
                <ChevronDown size={14} className={`transition-transform duration-200 ${photoDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {/* Dropdown Menu */}
              <div 
                className={`absolute top-full left-0 mt-2 py-2 min-w-[200px] rounded-lg shadow-lg transition-all duration-200 ${dropdownBg} ${
                  photoDropdownOpen ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-2'
                }`}
              >
                <Link
                  to="/photography/waste-workers-bahia"
                  className={`block px-4 py-2 text-sm ${dropdownText} hover:bg-white/10 transition-colors`}
                >
                  Waste Workers of Bahia
                </Link>
                <Link
                  to="/photography/children-of-lea"
                  className={`block px-4 py-2 text-sm ${dropdownText} hover:bg-white/10 transition-colors`}
                >
                  Children of the Lea
                </Link>
                <Link
                  to="/photography/wom-collective"
                  className={`block px-4 py-2 text-sm ${dropdownText} hover:bg-white/10 transition-colors`}
                >
                  WOM Collective
                </Link>
                <Link
                  to="/photography/portraits"
                  className={`block px-4 py-2 text-sm ${dropdownText} hover:bg-white/10 transition-colors`}
                >
                  Portraits
                </Link>
              </div>
            </div>

            <button
              onClick={() => scrollToSection('workshops')}
              data-testid="workshops-link"
              className={`nav-link text-sm uppercase tracking-wider ${textColor} hover:opacity-70 transition-all duration-300`}
            >
              Workshops
            </button>
          </nav>

          <Link
            to="/"
            data-testid="logo-link"
            className="absolute left-1/2 transform -translate-x-1/2"
          >
            <img
              src="https://customer-assets.emergentagent.com/job_8086167a-f51d-4ef8-80e1-c0055870858a/artifacts/xb9t18j2_logo%20amy%20dyduch%20signature_.png"
              alt="Amy Dyduch Logo"
              className="h-8 md:h-10 w-auto transition-all duration-300"
              style={{ filter: isLight ? 'brightness(0) invert(1)' : 'none' }}
            />
          </Link>

          <nav data-testid="right-nav" className="flex items-center gap-6">
            <button
              onClick={() => scrollToSection('about')}
              data-testid="about-link"
              className={`nav-link text-sm uppercase tracking-wider ${textColor} hover:opacity-70 transition-all duration-300`}
            >
              About
            </button>
            <button
              onClick={() => scrollToSection('contact')}
              data-testid="contact-link"
              className={`nav-link text-sm uppercase tracking-wider ${textColor} hover:opacity-70 transition-all duration-300`}
            >
              Contact
            </button>
            <div className="flex items-center gap-4 ml-2">
              <a
                href="https://www.instagram.com/dyduch/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="header-instagram-link"
                className={`${textColor} hover:opacity-70 transition-all duration-300`}
                aria-label="Instagram"
                onClick={(e) => {
                  e.stopPropagation();
                  window.open('https://www.instagram.com/dyduch/', '_blank');
                }}
              >
                <Instagram size={18} />
              </a>
              <a
                href="https://www.linkedin.com/in/amy-dyduch-b6b4b643/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="header-linkedin-link"
                className={`${textColor} hover:opacity-70 transition-all duration-300`}
                aria-label="LinkedIn"
                onClick={(e) => {
                  e.stopPropagation();
                  window.open('https://www.linkedin.com/in/amy-dyduch-b6b4b643/', '_blank');
                }}
              >
                <Linkedin size={18} />
              </a>
            </div>
          </nav>
        </div>
      </div>
    </header>
  );
};

export default Header;
